import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { cn } from "@/lib/util";
import {
  Globe, Server, ShieldCheck, Search, Building2, AlertCircle,
  Radar, Pencil, Loader2, ArrowRightLeft, X, Trash2,
} from "lucide-react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";

const TABS = [
  { id: "subdomains", label: "Subdominios", icon: Globe },
  { id: "services", label: "Servicios", icon: Server },
  { id: "certificates", label: "Certificados", icon: ShieldCheck },
];

// Renders a compact "Descubierto" / "Manual" badge based on the asset's origin.
function OriginBadge({ source }) {
  const isDiscovery = source === "asset_discovery";
  return (
    <span
      data-testid={`origin-badge-${isDiscovery ? "discovery" : "manual"}`}
      className={cn(
        "inline-flex items-center gap-1 px-1.5 py-0.5 border text-[9px] font-mono uppercase tracking-widest",
        isDiscovery
          ? "border-primary/50 text-primary bg-primary/10"
          : "border-border text-muted-foreground bg-muted/20"
      )}
    >
      {isDiscovery ? <Radar className="w-2.5 h-2.5" /> : <Pencil className="w-2.5 h-2.5" />}
      {isDiscovery ? "descubierto" : "manual"}
    </span>
  );
}

export default function Assets() {
  const [tab, setTab] = useState("subdomains");
  const [subs, setSubs] = useState([]);
  const [svcs, setSvcs] = useState([]);
  const [certs, setCerts] = useState([]);
  const [q, setQ] = useState("");
  const [running, setRunning] = useState(false);
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(() => {
    return sessionStorage.getItem("cibraxis:assets:client") || "";
  });
  const [loading, setLoading] = useState(false);

  // Reassign modal state
  const [reassignRow, setReassignRow] = useState(null);   // { kind, id?, value, name }
  const [reassignTarget, setReassignTarget] = useState("");
  const [reassignBusy, setReassignBusy] = useState(false);
  const [reassignConflict, setReassignConflict] = useState(null); // null | "reject" | "merge"

  const reload = async (clientId) => {
    if (!clientId) { setSubs([]); setSvcs([]); setCerts([]); return; }
    setLoading(true);
    const qs = `?client_id=${encodeURIComponent(clientId)}`;
    try {
      const [a, b, c] = await Promise.all([
        api.get(`/assets/subdomains${qs}`),
        api.get(`/assets/services${qs}`),
        api.get(`/assets/certificates${qs}`),
      ]);
      setSubs(a.data || []); setSvcs(b.data || []); setCerts(c.data || []);
    } finally { setLoading(false); }
  };

  useEffect(() => {
    api.get("/clients").then(r => {
      const list = r.data || [];
      setClients(list);
      if (!selectedClient && list.length > 0) {
        setSelectedClient(list[0].id);
        sessionStorage.setItem("cibraxis:assets:client", list[0].id);
      }
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { reload(selectedClient); }, [selectedClient]);

  const runDiscovery = async () => {
    if (!selectedClient) return;
    setRunning(true);
    try {
      const r = await api.post(
        `/scans/discovery?organization_id=${encodeURIComponent(selectedClient)}`,
      );
      toast.success(
        `Descubrimiento en cola · job ${r.data.job_id?.slice(0, 8) || "?"}. Recargo en 8 segundos…`,
      );
      setTimeout(() => reload(selectedClient).finally(() => setRunning(false)), 8000);
    } catch (ex) {
      toast.error(ex?.response?.data?.detail || "No se pudo lanzar el descubrimiento");
      setRunning(false);
    }
  };

  const deleteAsset = async (kind, row) => {
    const label = row.name || row.value || `${row.ip}:${row.port}` || row.subject;
    if (!window.confirm(`¿Dar de baja "${label}"?\n\nEsta acción no elimina el histórico de hallazgos, solo remueve el activo del inventario.`)) {
      return;
    }
    try {
      await api.delete(`/assets/${kind}/${encodeURIComponent(row.id)}`);
      toast.success(`Activo dado de baja: ${label}`);
      reload(selectedClient);
    } catch (ex) {
      toast.error(ex?.response?.data?.detail || "No se pudo eliminar el activo");
    }
  };

  const onClientChange = (id) => {
    setSelectedClient(id);
    if (id) sessionStorage.setItem("cibraxis:assets:client", id);
    else sessionStorage.removeItem("cibraxis:assets:client");
  };

  const currentClient = useMemo(
    () => clients.find(c => c.id === selectedClient),
    [clients, selectedClient]
  );

  const filtered = subs.filter(s => !q || s.name.toLowerCase().includes(q.toLowerCase()));

  const openReassign = (kind, row) => {
    setReassignRow({ kind, id: row.id, value: row.name || row.ip || row.value, ...row });
    setReassignTarget("");
    setReassignConflict(null);
  };

  const submitReassign = async (onConflict) => {
    if (!reassignRow || !reassignTarget) return;
    setReassignBusy(true);
    try {
      const payload = {
        kind: reassignRow.kind,
        target_organization_id: reassignTarget,
        on_conflict: onConflict || "reject",
      };
      if (reassignRow.kind === "ip") payload.value = reassignRow.value;
      else payload.asset_id = reassignRow.id;
      const r = await api.post("/assets/reassign", payload);
      toast.success(`Activo movido a ${r.data.target_organization_name || "cliente destino"}`);
      setReassignRow(null); setReassignTarget(""); setReassignConflict(null);
      await reload(selectedClient);
    } catch (ex) {
      const status = ex?.response?.status;
      const detail = ex?.response?.data?.detail;
      if (status === 409) {
        // Ask user how to resolve — option C from planning: modal decides.
        setReassignConflict("conflict");
      } else {
        toast.error(detail || "No se pudo reasignar el activo");
      }
    } finally { setReassignBusy(false); }
  };

  // Derive the IP list for the current client from organizations.ip_ranges
  // (services table is per-IP but sourced from Shodan scans; the client card
  // owns the "authorized ranges" list).
  const ipRows = useMemo(() => {
    if (!currentClient) return [];
    return (currentClient.ip_ranges || []).map(ip => ({ value: ip }));
  }, [currentClient]);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="assets-page">
      <header className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <div className="overline mb-2">Superficie de ataque</div>
          <h1 className="font-display text-4xl font-medium tracking-tight">Inventario de activos</h1>
          <p className="text-muted-foreground text-sm mt-1">
            Cada dominio, subdominio, servicio y certificado descubierto{" "}
            <span className="text-primary">por cliente</span>.
          </p>
        </div>
        <button
          onClick={runDiscovery}
          disabled={running || !selectedClient}
          className="border border-primary bg-primary/10 text-primary px-4 py-2 hover:bg-primary/20 disabled:opacity-50 font-mono text-xs tracking-widest uppercase"
          data-testid="run-discovery-btn"
        >
          {running ? "Descubrimiento en curso..." : "Ejecutar descubrimiento pasivo"}
        </button>
      </header>

      <section className="border border-border bg-card/60 p-4" data-testid="client-selector-section">
        <label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground flex items-center gap-2">
          <Building2 className="w-3 h-3" /> Cliente activo
        </label>
        <div className="mt-2 flex flex-col md:flex-row md:items-center gap-3">
          <select
            value={selectedClient}
            onChange={(e) => onClientChange(e.target.value)}
            data-testid="assets-client-select"
            className="flex-1 md:max-w-md bg-background border border-border px-3 py-2 text-sm font-mono focus:border-primary outline-none"
          >
            <option value="">— Selecciona un cliente —</option>
            {clients.map(c => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
          {currentClient && (
            <div className="text-xs font-mono text-muted-foreground">
              Dominios raíz:{" "}
              <span className="text-foreground">
                {(currentClient.root_domains || []).join(", ") || "—"}
              </span>
            </div>
          )}
        </div>
      </section>

      {!selectedClient ? (
        <div className="border border-sev-medium/40 bg-sev-medium/5 p-6 text-center text-sm text-sev-medium flex flex-col items-center gap-2"
             data-testid="no-client-selected">
          <AlertCircle className="w-6 h-6" />
          Selecciona un cliente para ver su inventario de activos.
        </div>
      ) : (
        <>
          <div className="flex items-center gap-2">
            {TABS.map(t => (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={cn(
                  "flex items-center gap-2 px-3 py-2 border font-mono text-[10px] tracking-widest uppercase",
                  tab === t.id ? "border-primary text-primary bg-primary/10" : "border-border text-muted-foreground hover:text-foreground"
                )}
                data-testid={`tab-${t.id}`}
              >
                <t.icon className="w-3 h-3" />{t.label}
              </button>
            ))}
            {loading && (
              <span className="ml-2 text-[10px] font-mono text-muted-foreground animate-pulse">
                Cargando…
              </span>
            )}
          </div>

          {tab === "subdomains" && (
            <>
              <div className="flex items-center gap-2 border border-border px-3 py-2 bg-background max-w-md">
                <Search className="w-4 h-4 text-muted-foreground" />
                <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="filtrar subdominios..."
                       className="bg-transparent border-none outline-none flex-1 font-mono text-sm" data-testid="search-subs" />
              </div>
              <div className="border border-border bg-card/60">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left overline border-b border-border">
                      <th className="p-3">Host</th>
                      <th className="p-3">Origen</th>
                      <th className="p-3">IPs</th>
                      <th className="p-3">Tecnología</th>
                      <th className="p-3">HTTP</th>
                      <th className="p-3">Confianza</th>
                      <th className="p-3">Primera vez visto</th>
                      <th className="p-3 text-right">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.length === 0 && (
                      <tr><td colSpan="8" className="p-8 text-center text-muted-foreground text-xs">
                        Sin subdominios para este cliente.
                      </td></tr>
                    )}
                    {filtered.map(s => (
                      <tr key={s.id} data-testid={`sub-row-${s.id}`} className="border-b border-border/50 hover:bg-muted/10">
                        <td className="p-3 font-mono text-xs">{s.name}</td>
                        <td className="p-3"><OriginBadge source={s.source} /></td>
                        <td className="p-3 font-mono text-[11px] text-muted-foreground">{s.ips?.join(", ") || "—"}</td>
                        <td className="p-3 text-xs">{s.technologies?.join(", ") || "—"}</td>
                        <td className="p-3 font-mono text-xs">{s.http_status ?? "—"}</td>
                        <td className="p-3 font-mono text-xs">{s.confidence}%</td>
                        <td className="p-3 font-mono text-[10px] text-muted-foreground">
                          {new Date(s.first_seen).toLocaleDateString()}
                        </td>
                        <td className="p-3 text-right">
                          <button
                            onClick={() => openReassign("subdomain", s)}
                            data-testid={`reassign-sub-${s.id}`}
                            className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-primary hover:underline"
                          >
                            <ArrowRightLeft className="w-3 h-3" /> Mover
                          </button>
                          <button
                            onClick={() => deleteAsset("subdomain", s)}
                            data-testid={`delete-sub-${s.id}`}
                            className="ml-3 inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-muted-foreground hover:text-sev-critical"
                            title="Dar de baja del inventario"
                          >
                            <Trash2 className="w-3 h-3" /> Baja
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Root domains + IP ranges of the current client so the operator
                  can reassign THEM too, not only subdomains. */}
              {currentClient && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <div className="border border-border bg-card/60">
                    <div className="overline p-3 border-b border-border">
                      Dominios raíz ({currentClient.root_domains?.length || 0})
                    </div>
                    {(currentClient.root_domains || []).length === 0 ? (
                      <div className="p-6 text-center text-xs text-muted-foreground">Sin dominios raíz.</div>
                    ) : (
                      <table className="w-full text-sm" data-testid="root-domains-table">
                        <tbody>
                          {(currentClient.domains || currentClient.root_domains.map(name => ({ id: name, name }))).map(d => (
                            <tr key={d.id || d.name} className="border-b border-border/50 hover:bg-muted/10">
                              <td className="p-3 font-mono text-xs">{d.name}</td>
                              <td className="p-3"><OriginBadge source={d.source} /></td>
                              <td className="p-3 text-right">
                                {d.id && (
                                  <button
                                    onClick={() => openReassign("domain", d)}
                                    data-testid={`reassign-domain-${d.id}`}
                                    className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-primary hover:underline"
                                  >
                                    <ArrowRightLeft className="w-3 h-3" /> Mover
                                  </button>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                  <div className="border border-border bg-card/60">
                    <div className="overline p-3 border-b border-border">
                      Rangos IP ({ipRows.length})
                    </div>
                    {ipRows.length === 0 ? (
                      <div className="p-6 text-center text-xs text-muted-foreground">Sin rangos IP.</div>
                    ) : (
                      <table className="w-full text-sm" data-testid="ip-ranges-table">
                        <tbody>
                          {ipRows.map(r => (
                            <tr key={r.value} className="border-b border-border/50 hover:bg-muted/10">
                              <td className="p-3 font-mono text-xs">{r.value}</td>
                              <td className="p-3 text-right">
                                <button
                                  onClick={() => openReassign("ip", { value: r.value, name: r.value })}
                                  data-testid={`reassign-ip-${r.value}`}
                                  className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-primary hover:underline"
                                >
                                  <ArrowRightLeft className="w-3 h-3" /> Mover
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                </div>
              )}
            </>
          )}

          {tab === "services" && (
            <div className="border border-border bg-card/60">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left overline border-b border-border">
                    <th className="p-3">IP</th>
                    <th className="p-3">Puerto</th>
                    <th className="p-3">Servicio</th>
                    <th className="p-3">Protocolo</th>
                    <th className="p-3">Estado</th>
                    <th className="p-3 text-right">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {svcs.length === 0 && (
                    <tr><td colSpan="6" className="p-8 text-center text-muted-foreground text-xs">
                      Sin servicios para este cliente. Ejecuta un escaneo Nmap o Naabu desde el módulo Escáneres para poblar esta tabla.
                    </td></tr>
                  )}
                  {svcs.map(s => (
                    <tr key={s.id} className="border-b border-border/50" data-testid={`svc-row-${s.id}`}>
                      <td className="p-3 font-mono text-xs">{s.ip}</td>
                      <td className={cn("p-3 font-mono text-xs", [3306,6379,21,23,445,3389,27017,9200].includes(s.port) && "text-sev-critical")}>{s.port}</td>
                      <td className="p-3 text-xs">{s.service_name || s.product || "—"}</td>
                      <td className="p-3 font-mono text-[11px] text-muted-foreground">{s.protocol}</td>
                      <td className="p-3 font-mono text-[10px] tracking-widest uppercase text-muted-foreground">{s.state}</td>
                      <td className="p-3 text-right">
                        <button
                          onClick={() => deleteAsset("service", s)}
                          data-testid={`delete-svc-${s.id}`}
                          className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-muted-foreground hover:text-sev-critical"
                          title="Dar de baja del inventario"
                        >
                          <Trash2 className="w-3 h-3" /> Baja
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {tab === "certificates" && (
            <div className="border border-border bg-card/60">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left overline border-b border-border">
                    <th className="p-3">Sujeto</th>
                    <th className="p-3">Emisor</th>
                    <th className="p-3">Vence</th>
                    <th className="p-3">Días restantes</th>
                    <th className="p-3 text-right">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {certs.length === 0 && (
                    <tr><td colSpan="5" className="p-8 text-center text-muted-foreground text-xs">
                      Sin certificados para este cliente. Ejecuta un escaneo testssl.sh sobre cualquier subdominio para poblar esta tabla.
                    </td></tr>
                  )}
                  {certs.map(c => (
                    <tr key={c.id} className="border-b border-border/50" data-testid={`cert-row-${c.id}`}>
                      <td className="p-3 font-mono text-xs">{c.subject}</td>
                      <td className="p-3 font-mono text-[11px] text-muted-foreground">{c.issuer}</td>
                      <td className="p-3 font-mono text-xs">{new Date(c.not_after).toLocaleDateString()}</td>
                      <td className={cn("p-3 font-mono text-sm font-bold",
                        c.days_until_expiry < 7 ? "text-sev-critical" :
                        c.days_until_expiry < 30 ? "text-sev-high" :
                        c.days_until_expiry < 60 ? "text-sev-medium" : "text-sev-low"
                      )}>{c.days_until_expiry}</td>
                      <td className="p-3 text-right">
                        <button
                          onClick={() => deleteAsset("certificate", c)}
                          data-testid={`delete-cert-${c.id}`}
                          className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-muted-foreground hover:text-sev-critical"
                          title="Dar de baja del inventario"
                        >
                          <Trash2 className="w-3 h-3" /> Baja
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* Reassign modal */}
      <Dialog open={!!reassignRow} onOpenChange={(v) => { if (!v) { setReassignRow(null); setReassignConflict(null); } }}>
        <DialogContent className="max-w-md" data-testid="reassign-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-display">
              <ArrowRightLeft className="w-4 h-4 text-primary" />
              Mover activo a otro cliente
            </DialogTitle>
            <DialogDescription>
              Vas a mover <span className="text-foreground font-mono">{reassignRow?.value || reassignRow?.name}</span>{" "}
              a otro cliente del mismo tenant. El histórico y los hallazgos no se transfieren.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-2">
            <div>
              <label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
                Cliente destino
              </label>
              <select
                value={reassignTarget}
                onChange={(e) => { setReassignTarget(e.target.value); setReassignConflict(null); }}
                data-testid="reassign-target-select"
                className="mt-1 w-full bg-background border border-border px-3 py-2 text-sm font-mono focus:border-primary outline-none"
              >
                <option value="">— Selecciona un cliente —</option>
                {clients.filter(c => c.id !== selectedClient).map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>

            {reassignConflict === "conflict" && (
              <div className="border border-sev-medium/40 bg-sev-medium/5 p-3 text-xs space-y-2" data-testid="reassign-conflict">
                <div className="text-sev-medium font-mono uppercase tracking-widest text-[10px]">
                  Conflicto detectado
                </div>
                <div className="text-foreground">
                  El cliente destino ya tiene ese activo. ¿Qué prefieres hacer?
                </div>
                <div className="flex flex-col sm:flex-row gap-2 pt-1">
                  <button
                    onClick={() => submitReassign("merge")}
                    disabled={reassignBusy}
                    data-testid="reassign-merge-btn"
                    className="flex-1 px-3 py-2 text-[10px] font-mono uppercase tracking-widest border border-primary text-primary hover:bg-primary hover:text-primary-foreground"
                  >
                    Fusionar (mantener el del destino)
                  </button>
                  <button
                    onClick={() => { setReassignConflict(null); setReassignRow(null); }}
                    className="flex-1 px-3 py-2 text-[10px] font-mono uppercase tracking-widest text-muted-foreground hover:text-foreground"
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            )}

            <div className="flex items-center justify-end gap-2 pt-2 border-t border-border">
              <button
                onClick={() => { setReassignRow(null); setReassignConflict(null); }}
                disabled={reassignBusy}
                className="px-3 py-2 text-xs font-mono uppercase tracking-widest text-muted-foreground hover:text-foreground"
              >
                Cancelar
              </button>
              <button
                onClick={() => submitReassign("reject")}
                disabled={reassignBusy || !reassignTarget || reassignConflict === "conflict"}
                data-testid="reassign-submit-btn"
                className={cn(
                  "px-4 py-2 flex items-center gap-2 text-xs font-mono uppercase tracking-widest border transition-colors",
                  reassignBusy || !reassignTarget || reassignConflict === "conflict"
                    ? "border-border text-muted-foreground cursor-not-allowed"
                    : "border-primary text-primary hover:bg-primary hover:text-primary-foreground"
                )}
              >
                {reassignBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <ArrowRightLeft className="w-3 h-3" />}
                Mover activo
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
